import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Card from "../../components/Common/Card";
import Table from "../../components/Common/Table";
import Button from "../../components/Common/Button";
import Select from "../../components/Common/Select";
import Input from "../../components/Common/Input";
import api from "../../utils/api";
import { showDeleteConfirm, showSuccess, showError } from "../../utils/swal";
import { Plus, Filter, X, Lock, Unlock, FileEdit, Image as ImageIcon } from "lucide-react";
import { usePermission } from "../../hooks/usePermission";
import { formatPrice, formatArea } from "../../utils/formatters";

const PortfolioFilteredList = ({ title, saleType, status, listingType, myPortfolio = false }) => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isPortfolioFullAccess } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermission();

  const canUserEdit = (row) => {
    if (!canEdit('portfolios')) return false;
    // Satılmış veya kiralanmış portföyler düzenlenemez
    if (row.sales && row.sales.length > 0) return false;
    if (row.rentals && row.rentals.length > 0) return false;
    return row.user_id === user?.id || user?.is_manager || user?.role === 'office_owner' || isPortfolioFullAccess();
  };

  const canUserDelete = (row) => {
    if (!canDelete('portfolios')) return false;
    return row.user_id === user?.id || user?.is_manager || user?.role === 'office_owner' || isPortfolioFullAccess();
  };

  // Dinamik opsiyonlar
  const [options, setOptions] = useState({
    property_types: [],
    residential_types: [],
    listing_types: [],
    sale_types: [],
    portfolio_statuses: [],
    room_definitions: [],
    facade_types: [],
    cities: [],
  });
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  // Filtreler: URL parametrelerinden oku
  const getFiltersFromURL = () => ({
    user_id: searchParams.get('user_id') || '',
    property_type_id: searchParams.get('property_type_id') || '',
    listing_type_id: searchParams.get('listing_type_id') || '',
    room_definition_id: searchParams.get('room_definition_id') || '',
    facade_type_id: searchParams.get('facade_type_id') || '',
    city_id: searchParams.get('city_id') || '',
    district_id: searchParams.get('district_id') || '',
    neighborhood_id: searchParams.get('neighborhood_id') || '',
    min_floor: searchParams.get('min_floor') || '',
    max_floor: searchParams.get('max_floor') || '',
    min_sqm: searchParams.get('min_sqm') || '',
    max_sqm: searchParams.get('max_sqm') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    min_building_age: searchParams.get('min_building_age') || '',
    max_building_age: searchParams.get('max_building_age') || '',
  });

  const [filters, setFilters] = useState(getFiltersFromURL);

  // URL'de filtre varsa filtre panelini otomatik aç
  useEffect(() => {
    const hasAnyFilter = Object.values(getFiltersFromURL()).some(v => v !== '');
    if (hasAnyFilter) {
      setShowFilters(true);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
  }, []);

  // URL parametreleri değiştiğinde filtreleri güncelle
  useEffect(() => {
    const newFilters = getFiltersFromURL();
    setFilters(newFilters);
  }, [searchParams]);

  // Filtreler değiştiğinde portföyleri getir
  useEffect(() => {
    fetchPortfolios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // İl değiştiğinde ilçeleri yükle
  useEffect(() => {
    if (filters.city_id) {
      fetchDistricts(filters.city_id);
    } else {
      setDistricts([]);
    }
  }, [filters.city_id]);

  // İlçe değiştiğinde mahalleleri yükle
  useEffect(() => {
    if (filters.district_id) {
      fetchNeighborhoods(filters.district_id);
    } else {
      setNeighborhoods([]);
    }
  }, [filters.district_id]);

  const fetchOptions = async () => {
    try {
      const response = await api.get('/portfolio-options');
      setOptions(response.data);

      // URL'de city_id varsa ilçeleri yükle
      const cityId = searchParams.get('city_id');
      if (cityId) fetchDistricts(cityId);

      const districtId = searchParams.get('district_id');
      if (districtId) fetchNeighborhoods(districtId);
    } catch (error) {
      console.error('Opsiyonlar yüklenemedi:', error);
    }
  };

  const fetchDistricts = async (cityId) => {
    try {
      const response = await api.get('/districts', { params: { city_id: cityId } });
      setDistricts(response.data);
    } catch (error) { console.error('İlçeler yüklenemedi:', error); }
  };


  const fetchNeighborhoods = async (districtId) => {
    try {
      const response = await api.get('/neighborhoods', { params: { district_id: districtId } });
      setNeighborhoods(response.data);
    } catch (error) { console.error('Mahalleler yüklenemedi:', error); }
  };

  const fetchPortfolios = async () => {
    try {
      // Boş olmayan filtreleri API'ye gönder
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '')
      );

      // Prop tabanlı filtreler (alt sayfa uyumluluğu - code bazlı)
      if (saleType) params.sale_type = saleType;
      if (status) params.status = status;
      if (listingType) params.listing_type = listingType;
      if (myPortfolio) params.my_portfolio = myPortfolio;

      const response = await api.get("/portfolios", { params });
      setPortfolios(response.data.data || response.data);
      setLoading(false);
    } catch (error) {
      console.error("Portföyler yüklenemedi:", error);
      setLoading(false);
    }
  };

  // Filtreyi değiştir ve URL'e yansıt
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    let newFilters = { ...filters, [name]: value };

    // İl değiştiğinde ilçe ve mahalle sıfırla
    if (name === 'city_id') {
      newFilters.district_id = '';
      newFilters.neighborhood_id = '';
    }
    // İlçe değiştiğinde mahalle sıfırla
    if (name === 'district_id') {
      newFilters.neighborhood_id = '';
    }

    setFilters(newFilters);
    syncFiltersToURL(newFilters);
  };

  // Filtreleri URL parametrelerine yaz
  const syncFiltersToURL = (filtersObj) => {
    const params = new URLSearchParams();
    Object.entries(filtersObj).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        params.set(key, value);
      }
    });
    setSearchParams(params, { replace: true });
  };

  // Filtreleri temizle
  const clearFilters = () => {
    const emptyFilters = {
      user_id: '',
      property_type_id: '',
      listing_type_id: '',
      room_definition_id: '',
      facade_type_id: '',
      city_id: '',
      district_id: '',
      neighborhood_id: '',
      min_floor: '',
      max_floor: '',
      min_sqm: '',
      max_sqm: '',
      min_price: '',
      max_price: '',
      min_building_age: '',
      max_building_age: '',
    };
    setFilters(emptyFilters);
    setSearchParams({}, { replace: true });
    setDistricts([]);
    setNeighborhoods([]);
  };

  // Aktif filtre sayısı
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const handleDelete = async (row) => {
    const result = await showDeleteConfirm(`"${row.title}" portföyü`);
    if (!result.isConfirmed) return;

    try {
      await api.delete(`/portfolios/${row.id}`);
      fetchPortfolios();
      showSuccess("Portföy başarıyla silindi!");
    } catch (error) {
      console.error("Portföy silinemedi:", error);
      showError("Portföy silinirken bir hata oluştu", "Silme Hatası");
    }
  };

  const columns = [
    {
      header: "Görsel",
      accessor: "cover_photo",
      render: (row) => (
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
          {row.cover_photo_url ? (
            <img 
              src={row.cover_photo_url} 
              alt={row.title} 
              className="w-full h-full object-cover" 
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-50">
              <ImageIcon size={16} />
            </div>
          )}
        </div>
      )
    },
    {
      header: "Başlık",
      accessor: "title",
      render: (row) => (
        <span
          className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer font-medium"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/portfolios/${row.id}`);
          }}
        >
          {row.title}
        </span>
      )
    },
    {
      header: "Emlak Tipi",
      accessor: "property_type",
      render: (row) => {
        const code = row.property_type?.code;
        const getPropertyTypeColor = (typeCode) => {
          switch (typeCode) {
            case 'residential': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'land': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'commercial': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPropertyTypeColor(code)}`}>
            {row.property_type?.name || "-"}
          </span>
        );
      },
    },
    {
      header: "Oda Sayısı",
      accessor: "room_definition",
      render: (row) => row.room_definition?.name || "-",
    },
    {
      header: "Konum",
      accessor: "city",
      render: (row) => {
        const parts = [];
        if (row.city?.name) parts.push(row.city.name);
        if (row.district?.name) parts.push(row.district.name);
        return parts.length > 0 ? parts.join(', ') : '-';
      },
    },
    {
      header: "Metrekare",
      accessor: "net_sqm",
      render: (row) => formatArea(row.net_sqm),
    },
    {
      header: "Danışman",
      accessor: "user",
      render: (row) => row.user?.name || "-",
    },
    {
      header: "Fiyat",
      accessor: "list_price",
      render: (row) => formatPrice(row.list_price),
    },
    {
      header: "Görünürlük",
      accessor: "visibility",
      render: (row) => {
        switch (row.visibility) {
          case 'shared':
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                <Unlock size={10} className="mr-1" />
                Paylaşımlı
              </span>
            );
          case 'private':
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800">
                <Lock size={10} className="mr-1" />
                Özel
              </span>
            );
          case 'draft':
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
                <FileEdit size={10} className="mr-1" />
                Taslak
              </span>
            );
          default:
            return "-";
        }
      },
    },
    {
      header: "Durum",
      accessor: "status",
      render: (row) => {
        const code = row.status?.code;
        const getStatusColor = (statusCode) => {
          switch (statusCode) {
            case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'deposit': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
            case 'option': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'manager_option': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            case 'sold': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
            case 'rented': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(code)}`}>
            {row.status?.name || "-"}
          </span>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{title}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Toplam {portfolios.length} portföy</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            Filtrele
            {activeFilterCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-600 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
          </Button>
          {canCreate('portfolios') && (
            <Button onClick={() => navigate("/portfolios/new")}>
              <Plus size={18} />
              Yeni Portföy
            </Button>
          )}
        </div>
      </div>

      {showFilters && (
        <Card className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Filtreler</h3>
            {activeFilterCount > 0 && (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                <X size={14} />
                Filtreleri Temizle
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Danışman filtresi */}
            <Select
              label="Danışman"
              name="user_id"
              value={filters.user_id}
              onChange={handleFilterChange}
              options={(options.users || []).map(u => ({ value: u.id, label: u.name }))}
            />

            {/* Satır 1: Tip filtreleri */}
            <Select
              label="Emlak Tipi"
              name="property_type_id"
              value={filters.property_type_id}
              onChange={handleFilterChange}
              options={options.property_types.map(t => ({ value: t.id, label: t.name }))}
            />
            <Select
              label="İlan Tipi"
              name="listing_type_id"
              value={filters.listing_type_id}
              onChange={handleFilterChange}
              options={options.listing_types.map(t => ({ value: t.id, label: t.name }))}
            />
            <Select
              label="Oda Sayısı"
              name="room_definition_id"
              value={filters.room_definition_id}
              onChange={handleFilterChange}
              options={options.room_definitions.map(t => ({ value: t.id, label: t.name }))}
            />
            <Select
              label="Cephe"
              name="facade_type_id"
              value={filters.facade_type_id}
              onChange={handleFilterChange}
              options={options.facade_types.map(t => ({ value: t.id, label: t.name }))}
            />

            {/* Satır 2: Konum */}
            <Select
              label="İl"
              name="city_id"
              value={filters.city_id}
              onChange={handleFilterChange}
              options={options.cities.map(c => ({ value: c.id, label: c.name }))}
            />
            <Select
              label="İlçe"
              name="district_id"
              value={filters.district_id}
              onChange={handleFilterChange}
              options={districts.map(d => ({ value: d.id, label: d.name }))}
            />
            <Select
              label="Mahalle"
              name="neighborhood_id"
              value={filters.neighborhood_id}
              onChange={handleFilterChange}
              options={neighborhoods.map(n => ({ value: n.id, label: n.name }))}
            />
            <div /> {/* Boş alan — grid hizası */}

            {/* Satır 3: Kat aralığı */}
            <Input
              label="Min Kat"
              name="min_floor"
              type="number"
              value={filters.min_floor}
              onChange={handleFilterChange}
              placeholder="Örn: 1"
            />
            <Input
              label="Max Kat"
              name="max_floor"
              type="number"
              value={filters.max_floor}
              onChange={handleFilterChange}
              placeholder="Örn: 10"
            />

            {/* Satır 3: Metrekare aralığı */}
            <Input
              label="Min Metrekare (m²)"
              name="min_sqm"
              type="number"
              value={filters.min_sqm}
              onChange={handleFilterChange}
              placeholder="Örn: 80"
            />
            <Input
              label="Max Metrekare (m²)"
              name="max_sqm"
              type="number"
              value={filters.max_sqm}
              onChange={handleFilterChange}
              placeholder="Örn: 200"
            />

            {/* Satır 4: Fiyat aralığı */}
            <Input
              label="Min Fiyat (₺)"
              name="min_price"
              type="number"
              value={filters.min_price}
              onChange={handleFilterChange}
              placeholder="Örn: 1000000"
            />
            <Input
              label="Max Fiyat (₺)"
              name="max_price"
              type="number"
              value={filters.max_price}
              onChange={handleFilterChange}
              placeholder="Örn: 5000000"
            />

            {/* Satır 4: Bina yaşı */}
            <Input
              label="Min Bina Yaşı"
              name="min_building_age"
              type="number"
              value={filters.min_building_age}
              onChange={handleFilterChange}
              placeholder="Örn: 0"
            />
            <Input
              label="Max Bina Yaşı"
              name="max_building_age"
              type="number"
              value={filters.max_building_age}
              onChange={handleFilterChange}
              placeholder="Örn: 10"
            />
          </div>
        </Card>
      )}

      <Card noPadding>
        <Table
          columns={columns}
          data={portfolios}
          onView={(row) => navigate(`/portfolios/${row.id}`)}
          onEdit={(row) => navigate(`/portfolios/${row.id}/edit`)}
          onDelete={handleDelete}
          canEdit={canUserEdit}
          canDelete={canUserDelete}
          rowClassName={(row) => row.visibility === 'draft' ? 'opacity-60 grayscale-[0.5]' : ''}
        />
      </Card>
    </div>
  );
};

export default PortfolioFilteredList;
